import { TRPCClientError } from '@trpc/client';
import type { AppRouter } from '@tagadapay/trpc-types';

export interface ParsedTRPCError {
    message: string;
    code?: string;
    fieldErrors?: Record<string, string[]>;
    isValidationError: boolean;
}

/**
 * Parses TRPC errors to extract meaningful error information
 * Handles both validation errors (with field-specific errors) and general errors
 */
export function parseTRPCError(error: unknown): ParsedTRPCError {
    // Handle TRPC client errors
    if (error instanceof TRPCClientError) {
        const data = error.data;

        // Check if it's a validation error with zodError
        if (data?.zodError?.fieldErrors) {
            return {
                message: 'Please fix the validation errors below',
                code: data.code,
                fieldErrors: data.zodError.fieldErrors,
                isValidationError: true,
            };
        }

        // Handle other TRPC errors
        return {
            message: error.message || 'An error occurred',
            code: data?.code,
            fieldErrors: {},
            isValidationError: false,
        };
    }

    // Handle generic errors
    if (error instanceof Error) {
        return {
            message: error.message,
            fieldErrors: {},
            isValidationError: false,
        };
    }

    // Handle unknown error types
    return {
        message: 'An unexpected error occurred',
        fieldErrors: {},
        isValidationError: false,
    };
}

/**
 * Gets a user-friendly error message for a specific field
 */
export function getFieldErrorMessage(fieldErrors: Record<string, string[]>, fieldName: string): string | undefined {
    const errors = fieldErrors[fieldName];
    return errors && errors.length > 0 ? errors[0] : undefined;
}

/**
 * Checks if there are any field errors
 */
export function hasFieldErrors(fieldErrors: Record<string, string[]>): boolean {
    return Object.keys(fieldErrors).length > 0;
}

/**
 * Gets all field error messages as a flat array
 */
export function getAllFieldErrorMessages(fieldErrors: Record<string, string[]>): string[] {
    return Object.values(fieldErrors).flat();
}
